import React, { useEffect, useState } from "react";
import { TrendingUpIcon, RefreshCwIcon } from "lucide-react";
import { Button, Table, Progress, Alert } from "antd";
import {
  LoadingOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import "./ChartModal.css";
import { useGeoLiftForm } from "../../../../hooks/useGeoLiftForm";
import useGeoLiftDataProcessor, {
  ResponseData,
  MarketResult,
  isGeoLiftResponse,
  formatMarketName,
} from "./GeoLiftDataProcessor";
import MarketDetailsModal from "./MarketDetailsModal";
import EnhancedLoadingIndicator from "../../../../components/EnhancedLoadingIndicator";
import ReactECharts from "echarts-for-react";

interface ViewResultsProps {
  jobConfigId?: string;
  setPostTestData?: React.Dispatch<React.SetStateAction<any>>;
  resultData: ResponseData | null;
  mode: any;
  isSales?: boolean;
}

const ViewResults: React.FC<ViewResultsProps> = ({
  jobConfigId,
  setPostTestData,
  resultData,
  mode,
  isSales: isSalesProp,
}) => {

  const {
    form,
    loading,
    testMarkets,
    syntheticMarketOptions,
    handleTestMarketChange,
  } = useGeoLiftForm(jobConfigId || "", setPostTestData);

  const [currentSyntheticMarket, setCurrentSyntheticMarket] =
    useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<string>("");
  const [currentMarketData, setCurrentMarketData] =
    useState<MarketResult | null>(null);

  // Update useEffect to set initial synthetic market when options change
  useEffect(() => {
    if (syntheticMarketOptions && syntheticMarketOptions.length > 0) {
      setCurrentSyntheticMarket(syntheticMarketOptions[0].label);
    }
  }, [syntheticMarketOptions]);

  const isSales = isSalesProp ?? (resultData?.isSales ?? true);
  const dollarPrefix = isSales ? "$" : "";

  const getCssVar = (name: string, fallback: string) => {
    if (typeof window === "undefined") return fallback;
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    return value || fallback;
  };

  const chartAxisColor = getCssVar("--text-secondary", "#94A3B8");
  const chartLabelColor = getCssVar("--text-primary", "#F0F4F8");
  const chartGridColor = getCssVar("--border-subtle", "#1E2A3E");
  const chartControlColor = getCssVar("--text-tertiary", "#64748B");
  const chartTestColor = getCssVar("--blue", "#3B82F6");

  // Use the data processor hook
  const {
    marketResults,
    batchProcessing,
    currentProcessingIndex,
    aggregateData,
    processAllMarkets,
  } = useGeoLiftDataProcessor({
    jobConfigId,
    form,
    testMarkets,
    currentSyntheticMarket,
    handleTestMarketChange,
    mode,
  });

  // Function to render the aggregate data summary card
  const renderAggregateDataSummary = (): React.ReactNode => {
    if (!aggregateData) return null;

    // Prepare bar chart data
    const controlValue = Math.round(
      aggregateData?.lift?.totalSalesWithoutCampaign ?? 0
    );
    const testValue = Math.round(
      aggregateData?.lift?.totalSalesWithCampaign ?? 0
    );

    // roas.totalIncrementSales is only present when ad-spend data was
    // available to compute ROAS. The incremental outcome itself doesn't
    // depend on ad spend, so derive it from lift (always present) and fall
    // back to roas only if lift is somehow missing.
    const incrementalOutcomeValue = aggregateData?.lift
      ? testValue - controlValue
      : Math.round(aggregateData?.roas?.totalIncrementSales ?? 0);

    // Format value for display
    const formatValue = (value: number): string => {
      if (value >= 1_000_000) {
        return (value / 1_000_000).toFixed(1) + "M";
      } else if (value >= 1_000) {
        return (value / 1_000).toFixed(1) + "K";
      }
      return value.toLocaleString();
    };

    // Horizontal bar chart configuration
    const chartOption = {
      grid: {
        left: "15%",
        right: "10%",
        top: "10%",
        bottom: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLabel: {
          formatter: (value: number) => `${dollarPrefix}${formatValue(value)}`,
          fontSize: 12,
          color: chartAxisColor,
        },
        splitLine: {
          lineStyle: {
            color: chartGridColor,
          },
        },
      },
      yAxis: {
        type: "category",
        data: ["Control", "Test"],
        axisLabel: {
          fontSize: 14,
          color: chartLabelColor,
          fontWeight: 500,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      series: [
        {
          type: "bar",
          data: [
            {
              value: controlValue,
              itemStyle: { color: chartControlColor }, // neutral for control
            },
            {
              value: testValue,
              itemStyle: { color: chartTestColor }, // brand blue for test
            },
          ],
          barWidth: "50%",
          label: {
            show: true,
            position: "right",
            formatter: (params: any) => `${dollarPrefix}${formatValue(params.value)}`,
            fontSize: 12,
            fontWeight: "bold",
            color: chartLabelColor,
          },
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any) => {
          const param = params[0];
          return `${param.name}: ${dollarPrefix}${param.value.toLocaleString()}`;
        },
      },
    };

    return (
      <div
        className="mb-6 p-3 rounded-xl shadow-sm border-2"
        style={{ background: "var(--bg-elevated)", borderColor: "var(--border-subtle)" }}
      >
        <div className="flex justify-center items-center mb-4">
          <h3 className="text-lg font-semibold text-[var(--text-primary)] text-center">
            {testMarkets?.length === 1
              ? "Campaign Results"
              : "Aggregate Campaign Results"}
          </h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left side - Metric cards stacked vertically */}
          <div className="lg:col-span-1 flex flex-col gap-4">
            <div className="bg-[var(--blue)] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center mb-2">
                  <TrendingUpIcon className="h-5 w-5 mr-2 text-white/80" />
                  <span className="font-medium text-white/90">Total Lift</span>
                </div>
                <div
                  className={`text-2xl font-bold ${
                    (aggregateData?.lift?.totalLiftPercentage ?? 0) >= 0
                      ? "text-[var(--success)]"
                      : "text-[var(--error)]"
                  }`}
                >
                  {(aggregateData?.lift?.totalLiftPercentage ?? 0) >= 0
                    ? "+"
                    : ""}
                  {aggregateData?.lift?.totalLiftPercentage.toFixed(2)}%
                </div>
              </div>
            </div>

            <div className="bg-[var(--blue)] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <span className="font-medium text-white/90 mb-2">
                  Incremental Outcome
                </span>
                <div className="text-2xl font-bold text-white">
                  {dollarPrefix}
                  {incrementalOutcomeValue.toLocaleString("en-US")}
                </div>
              </div>
            </div>

            {isSales && aggregateData?.roas && (
              <div className="bg-[var(--blue)] p-4 rounded-lg">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center mb-2">
                    {/* <DollarSignIcon className="h-5 w-5 text-white/80 mr-2" /> */}
                    <span className="font-medium text-white/90">iROAS</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {aggregateData.roas.totalROAS.toFixed(2)}x
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right side - Horizontal bar chart */}
          <div className="lg:col-span-2 bg-[var(--bg-surface)] p-4 rounded-lg flex flex-col">
            <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-3 text-center">
              Control vs. Test Outcome
            </h4>
            <div className="flex-1">
              <ReactECharts
                option={chartOption}
                style={{ height: "100%", minHeight: "200px" }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-0" style={{ background: "var(--bg-base)", color: "var(--text-primary)" }}>
      {loading ? (
        <EnhancedLoadingIndicator mode={false} />
      ) : (
        <div className="space-y-3">
          <div>
            {marketResults.length > 0 && (
              <div className="mt-0 p-1 rounded-xl shadow-sm" style={{ background: "var(--bg-elevated)" }}>
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                    All Markets Results
                  </h3>
                  <div className="flex gap-2 mb-4">
                    <Button
                      type="primary"
                      onClick={() => processAllMarkets()}
                      disabled={batchProcessing}
                      className="py-4 text-lg"
                      icon={
                        batchProcessing ? (
                          <SyncOutlined spin />
                        ) : (
                          <RefreshCwIcon className="h-4 w-4 mr-2" />
                        )
                      }
                      loading={batchProcessing}
                      size="large"
                    >
                      {batchProcessing
                        ? "Processing Markets..."
                        : "Process All Markets"}
                    </Button>
                  </div>
                </div>
                {/* Render aggregate data summary if available */}
                {aggregateData && renderAggregateDataSummary()}

                <div>
                  {batchProcessing && (
                    <div className="mt-4 p-4 rounded-lg shadow-sm" style={{ background: "var(--blue-subtle)" }}>
                      <div className="flex items-center mb-3">
                        <LoadingOutlined
                          style={{
                            fontSize: 24,
                            color: "var(--blue)",
                            marginRight: 12,
                          }}
                          spin
                        />
                        <div className="text-[var(--blue)] font-medium">
                          Processing Markets: {currentProcessingIndex + 1} of{" "}
                          {marketResults.length}
                        </div>
                      </div>
                      <div className="mb-3">
                        <Progress
                          percent={Math.round(
                            ((currentProcessingIndex + 1) /
                              marketResults.length) *
                              100
                          )}
                          status="active"
                          strokeColor={{
                            "0%": "var(--blue)",
                            "100%": "var(--success)",
                          }}
                        />
                      </div>
                      <div className="text-[var(--text-secondary)] text-sm">
                        Currently processing:{" "}
                        <span className="font-medium text-[var(--blue)]">
                          {marketResults[currentProcessingIndex]?.marketName
                            ? formatMarketName(
                                marketResults[currentProcessingIndex].marketName
                              )
                            : "Preparing..."}
                        </span>
                      </div>
                    </div>
                  )}

                  {!batchProcessing &&
                    marketResults.some(
                      (result) => result.status === "error"
                    ) && (
                      <Alert
                        message="Processing Errors"
                        description={
                          <div>
                            <div className="font-medium mb-1">
                              The following markets encountered errors:
                            </div>
                            <ul className="list-disc pl-5">
                              {marketResults
                                .filter((r) => r.status === "error")
                                .map((market, index) => (
                                  <li key={index}>
                                    {formatMarketName(market.marketName)}:{" "}
                                    {market.error || "Unknown error"}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        }
                        type="error"
                        showIcon
                        icon={<WarningOutlined />}
                        className="mt-4"
                      />
                    )}
                </div>
                <Table
                  dataSource={marketResults}
                  rowKey="marketName"
                  pagination={false}
                  className="mt-4"
                  columns={[
                    {
                      title: "#",
                      dataIndex: "index",
                      key: "marketName",
                      render: (index) => (
                        <span className="font-medium">{index}</span>
                      ),
                    },
                    {
                      title: "Test Markets",
                      dataIndex: "marketName",
                      key: "marketName",
                      render: (text) => (
                        <span className="font-medium">
                          {formatMarketName(text)}
                        </span>
                      ),
                    },
                    {
                      title: "Incremental outcome",
                      key: "incremental",
                      render: (_, record) => {
                        if (record.status !== "completed" || !record.data) {
                          return "-";
                        }

                        try {
                          let sales;
                          // Check if data is in the expected format
                          if (
                            record.data.data &&
                            typeof record.data.data === "object"
                          ) {
                            // API response structure
                            const responseData = record.data.data;

                            if (isGeoLiftResponse(responseData)) {
                              sales = responseData.incremental;
                            } else if (
                              responseData.lift &&
                              typeof responseData.lift === "object"
                            ) {
                              sales = responseData.lift.incremental;
                            } else {
                              return "-";
                            }
                          } else {
                            // Direct data structure (fallback)
                            if (isGeoLiftResponse(record.data)) {
                              sales = record.data.incremental;
                            } else if (
                              record.data.lift &&
                              typeof record.data.lift === "object"
                            ) {
                              sales = record.data.lift.incremental;
                            } else {
                              return "-";
                            }
                          }

                          if (typeof sales !== "number") {
                            return "-";
                          }

                          return `${dollarPrefix}${Math.round(sales).toLocaleString()}`;
                        } catch (error) {
                          return "-";
                        }
                      },
                    },
                    {
                      title: "Lift",
                      key: "lift",
                      render: (_, record) => {
                        if (record.status !== "completed" || !record.data) {
                          return "-";
                        }

                        let liftPercentage;
                        try {
                          // Check if data is in the expected format
                          if (
                            record.data.data &&
                            typeof record.data.data === "object"
                          ) {
                            // API response structure
                            const responseData = record.data.data;

                            if (isGeoLiftResponse(responseData)) {
                              liftPercentage = responseData.lift_percentage;
                            } else if (
                              responseData.lift &&
                              typeof responseData.lift === "object"
                            ) {
                              liftPercentage =
                                responseData.lift.totalLiftPercentage;
                            } else {
                              return "-";
                            }
                          } else {
                            // Direct data structure (fallback)
                            if (isGeoLiftResponse(record.data)) {
                              liftPercentage = record.data.lift_percentage;
                            } else if (
                              record.data.lift &&
                              typeof record.data.lift === "object"
                            ) {
                              liftPercentage =
                                record.data.lift.totalLiftPercentage;
                            } else {
                              return "-";
                            }
                          }

                          if (typeof liftPercentage !== "number") {
                            return "-";
                          }

                          return (
                            <span
                              className={`font-medium ${
                                liftPercentage >= 0
                                  ? "text-[var(--success)]"
                                  : "text-[var(--error)]"
                              }`}
                            >
                              {liftPercentage.toFixed(2)}%
                            </span>
                          );
                        } catch (error) {
                          console.error("Error rendering lift %:", error);
                          return "-";
                        }
                      },
                    },

                    {
                      title: "Statistical Significance",
                      key: "salesWithoutCampaign",
                      render: (_, record) => {
                        if (record.status !== "completed" || !record.data) {
                          return "-";
                        }

                        try {
                          let sales;
                          // Check if data is in the expected format
                          if (
                            record.data.data &&
                            typeof record.data.data === "object"
                          ) {
                            // API response structure
                            const responseData = record.data.data;

                            if (isGeoLiftResponse(responseData)) {
                              sales = responseData.pvalue;
                            } else if (
                              responseData.lift &&
                              typeof responseData.lift === "object"
                            ) {
                              sales = responseData.lift.pvalue;
                            } else {
                              return "-";
                            }
                          } else {
                            // Direct data structure (fallback)
                            if (isGeoLiftResponse(record.data)) {
                              sales = record.data.pvalue;
                            } else if (
                              record.data.lift &&
                              typeof record.data.lift === "object"
                            ) {
                              sales = record.data.lift.pvalue;
                            } else {
                              return "-";
                            }
                          }

                          if (typeof sales !== "number") {
                            return "-";
                          }

                          return <span>{((1 - sales) * 100).toFixed(2)}%</span>;
                        } catch (error) {
                          return "-";
                        }
                      },
                    },
                    {
                      title: "",
                      key: "action",
                      render: (_, record) => {
                        if (record.status !== "completed") {
                          return null;
                        }

                        return (
                          <Button
                            type="primary"
                            onClick={() => {
                              // Set the current market data for the modal
                              setModalContent(record.marketName);
                              setCurrentMarketData(record.data);
                              // // Open the modal
                              setIsModalOpen(true);
                            }}
                          >
                            View Details
                          </Button>
                        );
                      },
                    },
                  ]}
                />
              </div>
            )}
          </div>
          {/* Market Details Modal */}
          <MarketDetailsModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            currentMarketData={currentMarketData}
            modalContent={modalContent}
            dollarPrefix={dollarPrefix}
          />
        </div>
      )}
    </div>
  );
};

export default ViewResults;
